import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ExternalLink, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("ro-RO", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

export const AdminDashboard = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingComments, setPendingComments] = useState(0);
  const [subscriberCount, setSubscriberCount] = useState(null);
  const [newMessages, setNewMessages] = useState(0);
  const [newQuestions, setNewQuestions] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/posts");
      setPosts(data);
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut încărca însemnările"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/admin/messages").then((r) => setNewMessages(r.data.filter((m) => m.status === "nou").length)).catch(() => {});
    api.get("/admin/questions").then((r) => setNewQuestions(r.data.filter((q) => q.status === "nouă").length)).catch(() => {});
    api
      .get("/admin/comments")
      .then((r) => setPendingComments(r.data.filter((c) => !c.approved).length))
      .catch(() => {});
    api
      .get("/admin/subscribers")
      .then((r) => setSubscriberCount(r.data.filter((s) => s.active !== false).length))
      .catch(() => {});
  }, []);

  const remove = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Ștergi această însemnare? Acțiunea nu poate fi anulată.")) return;
    try {
      await api.delete(`/admin/posts/${id}`);
      setPosts((p) => p.filter((x) => x.id !== id));
      toast.success("Însemnare ștearsă");
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut șterge însemnarea"));
    }
  };

  const togglePublish = async (post) => {
    try {
      const { data } = await api.put(`/admin/posts/${post.id}`, { published: !post.published });
      setPosts((all) => all.map((p) => (p.id === post.id ? data : p)));
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut actualiza însemnarea"));
    }
  };

  return (
    <main data-testid="admin-dashboard" className="mx-auto max-w-6xl px-6 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[color-mix(in_srgb,var(--milk)_18%,transparent)] pb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">Atelier</p>
          <h1 className="mt-2 font-serif text-4xl font-light text-[#F7F1E8]">
            Bine ai revenit, {user?.name || "Alexia"}.
          </h1>
          <p className="mt-2 text-sm text-[#F7F1E8]/78">
            {posts.length} {posts.length === 1 ? "însemnare" : "însemnări"} în jurnal.
          </p>
        </div>
        <Link
          to="/admin/new"
          data-testid="admin-new-post-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-5 py-3 text-xs font-medium uppercase tracking-[0.2em] text-white transition-transform hover:-translate-y-0.5 active:scale-95"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={1.6} /> Însemnare nouă
        </Link>
      </div>

      <Link
        to="/admin/asistent"
        data-testid="admin-assistant-link"
        className="mt-8 flex items-center justify-between rounded-2xl border border-[color-mix(in_srgb,var(--accent)_25%,transparent)] bg-[var(--tint-pink)] px-6 py-5 transition-transform hover:-translate-y-0.5"
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--accent)]">Asistent AI · nou</p>
          <p className="mt-1 font-serif text-xl text-[#F7F1E8]">Partenerul tău de scris — creează însemnări la comandă</p>
        </div>
        <span className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">deschide &rarr;</span>
      </Link>

      <Link
        to="/admin/personalizare"
        data-testid="admin-personalize-link"
        className="mt-4 flex items-center justify-between rounded-2xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[var(--tint-butter)] px-6 py-5 transition-transform hover:-translate-y-0.5"
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--sun-dark)]">Personalizare · nou</p>
          <p className="mt-1 font-serif text-xl text-[#F7F1E8]">Fundaluri cu cafea, texte &amp; rame foto</p>
        </div>
        <span className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">deschide &rarr;</span>
      </Link>

      <Link
        to="/admin/stats"
        data-testid="admin-stats-link"
        className="mt-4 flex items-center justify-between rounded-2xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[var(--tint-mint)] px-6 py-5 transition-transform hover:-translate-y-0.5"
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--teal)]">Statistici</p>
          <p className="mt-1 font-serif text-xl text-[#F7F1E8]">Vizite, inimi &amp; note pe fiecare însemnare</p>
        </div>
        <span className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">deschide &rarr;</span>
      </Link>

      <Link
        to="/admin/comments"
        data-testid="admin-comments-link"
        className="mt-4 flex items-center justify-between rounded-2xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[var(--tint-mint)] px-6 py-5 transition-transform hover:-translate-y-0.5"
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--teal)]">Moderare</p>
          <p className="mt-1 font-serif text-xl text-[#F7F1E8]">Notele cititorilor</p>
        </div>
        <span className="flex items-center gap-3">
          {pendingComments > 0 && (
            <span
              data-testid="pending-comments-badge"
              className="rounded-full bg-[var(--sun)] px-3 py-1 text-xs font-semibold text-[var(--milk)]"
            >
              {pendingComments} în așteptare
            </span>
          )}
          <span className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">deschide &rarr;</span>
        </span>
      </Link>

      <Link
        to="/admin/subscribers"
        data-testid="admin-subscribers-link"
        className="mt-4 flex items-center justify-between rounded-2xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[var(--tint-butter)] px-6 py-5 transition-transform hover:-translate-y-0.5"
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--sun-dark)]">Abonați</p>
          <p className="mt-1 font-serif text-xl text-[#F7F1E8]">Cititorii abonați</p>
        </div>
        <span className="flex items-center gap-3">
          <span data-testid="subscriber-count-badge" className="rounded-full bg-[var(--accent)] px-3 py-1 text-xs font-semibold text-white">
            {subscriberCount === null ? "…" : subscriberCount}
          </span>
          <span className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">deschide &rarr;</span>
        </span>
      </Link>

      <Link
        to="/admin/mesaje"
        data-testid="admin-messages-link"
        className="mt-4 flex items-center justify-between rounded-2xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[var(--tint-pink)] px-6 py-5 transition-transform hover:-translate-y-0.5"
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--accent-dark)]">Hai la o cafea</p>
          <p className="mt-1 font-serif text-xl text-[#F7F1E8]">Mesajele private de la cititori</p>
        </div>
        <span className="flex items-center gap-3">
          {newMessages > 0 && <span data-testid="admin-messages-badge" className="rounded-full bg-[var(--accent)] px-2.5 py-0.5 text-[11px] font-semibold text-white">{newMessages} noi</span>}
          <span className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">deschide &rarr;</span>
        </span>
      </Link>

      <Link
        to="/admin/intrebari"
        data-testid="admin-questions-link"
        className="mt-4 flex items-center justify-between rounded-2xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[var(--tint-mint)] px-6 py-5 transition-transform hover:-translate-y-0.5"
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--teal-dark)]">Cutia cu întrebări anonime</p>
          <p className="mt-1 font-serif text-xl text-[#F7F1E8]">Întrebări la care poți răspunde public</p>
        </div>
        <span className="flex items-center gap-3">
          {newQuestions > 0 && <span data-testid="admin-questions-badge" className="rounded-full bg-[var(--teal)] px-2.5 py-0.5 text-[11px] font-semibold text-white">{newQuestions} noi</span>}
          <span className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">deschide &rarr;</span>
        </span>
      </Link>

      <Link
        to="/admin/newsletter"
        data-testid="admin-newsletter-link"
        className="mt-4 flex items-center justify-between rounded-2xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[var(--tint-mint)] px-6 py-5 transition-transform hover:-translate-y-0.5"
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--teal-dark)]">Newsletter</p>
          <p className="mt-1 font-serif text-xl text-[#F7F1E8]">Emailul de vineri — previzualizează și editează</p>
        </div>
        <span className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">deschide &rarr;</span>
      </Link>

      <Link
        to="/admin/settings"
        data-testid="admin-settings-link"
        className="mt-4 flex items-center justify-between rounded-2xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[var(--tint-peach)] px-6 py-5 transition-transform hover:-translate-y-0.5"
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-[var(--coral)]">Setări</p>
          <p className="mt-1 font-serif text-xl text-[#F7F1E8]">Schimbă pozele site-ului</p>
        </div>
        <span className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">deschide &rarr;</span>
      </Link>

      {loading ? (
        <p className="py-16 text-center text-sm text-[#F7F1E8]/68" data-testid="admin-loading">
          Îți aduc însemnările&hellip;
        </p>
      ) : (
        <ul className="mt-8 divide-y divide-[color-mix(in_srgb,var(--milk)_18%,transparent)]" data-testid="admin-posts-list">
          {posts.map((p) => (
            <li
              key={p.id}
              data-testid={`admin-post-row-${p.id}`}
              className="flex flex-wrap items-center justify-between gap-4 py-5"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="font-serif text-xl font-medium text-[#F7F1E8]">{p.title}</h3>
                  {!p.published && p.scheduled_for && (
                    <span
                      data-testid={`scheduled-badge-${p.id}`}
                      className="rounded-full bg-[var(--tint-mint)] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] text-[var(--teal)]"
                    >
                      Programată · {new Date(p.scheduled_for).toLocaleString("ro-RO", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                  {!p.published && !p.scheduled_for && (
                    <span className="rounded-full bg-[var(--tint-butter)] px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] text-[var(--sun)]">
                      Ciornă
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/58">
                  {fmt(p.created_at)} &middot; /{p.slug}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => togglePublish(p)}
                  data-testid={`admin-toggle-${p.id}`}
                  className="rounded-full p-2 text-[#F7F1E8]/78 hover:bg-[var(--tint-mint)] hover:text-[#F7F1E8]"
                  aria-label={p.published ? "Retrage din publicare" : "Publică"}
                  title={p.published ? "Retrage din publicare" : "Publică"}
                >
                  {p.published ? <Eye className="h-4 w-4" strokeWidth={1.6} /> : <EyeOff className="h-4 w-4" strokeWidth={1.6} />}
                </button>
                {p.published && (
                  <a
                    href={`/post/${p.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`admin-view-${p.id}`}
                    className="rounded-full p-2 text-[#F7F1E8]/78 hover:bg-[var(--tint-peach)] hover:text-[#F7F1E8]"
                    aria-label="Vezi"
                    title="Vezi"
                  >
                    <ExternalLink className="h-4 w-4" strokeWidth={1.6} />
                  </a>
                )}
                <Link
                  to={`/admin/edit/${p.id}`}
                  data-testid={`admin-edit-${p.id}`}
                  className="rounded-full p-2 text-[#F7F1E8]/78 hover:bg-[var(--tint-pink)] hover:text-[#F7F1E8]"
                  aria-label="Editează"
                  title="Editează"
                >
                  <Pencil className="h-4 w-4" strokeWidth={1.6} />
                </Link>
                <button
                  onClick={() => remove(p.id)}
                  data-testid={`admin-delete-${p.id}`}
                  className="rounded-full p-2 text-[#F7F1E8]/78 hover:bg-red-50 hover:text-red-500"
                  aria-label="Șterge"
                  title="Șterge"
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.6} />
                </button>
              </div>
            </li>
          ))}
          {posts.length === 0 && (
            <li className="py-16 text-center" data-testid="admin-empty">
              <p className="font-serif text-2xl text-[#F7F1E8]">O pagină albă.</p>
              <p className="mt-2 text-sm text-[#F7F1E8]/78">Prima însemnare abia așteaptă să fie scrisă.</p>
            </li>
          )}
        </ul>
      )}
    </main>
  );
};

export default AdminDashboard;
;
